/**
 * @file 사내 게시판 — 좌측 게시판 사이드탭 + 우측 본문(홈 / 목록 / 상세).
 *
 * <p>예전엔 '공지 채널' 이 이 역할을 했다. 채널은 멤버만 보는 구조여서 <b>전사에 알려야 하는 글</b>과
 * 맞지 않았고(초대되지 않은 사람은 공지를 볼 수 없었다), 파일·양식을 찾으려면 채널을 뒤져야 했다.
 * 게시판은 멤버십 개념 없이 전 직원이 같은 목록을 본다.
 *
 * <h3>주소가 화면 상태의 단일 출처</h3>
 * <pre>
 *   /board                        → 전체 게시판(홈)
 *   /board/{typeId}               → 그 게시판의 글 목록
 *   /board/{typeId}/{postId}      → 글 상세
 * </pre>
 * <p>탭을 클릭해도 주소를 갱신한다 — 그래야 뒤로 가기가 동작하고 글 링크를 그대로 공유할 수 있다.
 * (채널이 {@code /channel/:channelId} 로 같은 규약을 쓴다.)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Box } from "@mui/material";

import BoardSidebar from "./components/BoardSidebar";
import BoardHome from "./components/BoardHome";
import BoardPostList from "./components/BoardPostList";
import BoardPostDetail from "./components/BoardPostDetail";
import BoardPostComposeDialog from "./components/BoardPostComposeDialog";
import BoardTypeDialog from "./components/BoardTypeDialog";
import LoadingDots from "../../component/common/LoadingDots";
import { boardApi } from "./api/boardApi";
import { api } from "../../util/api";
import { notify } from "../../util/toast";
import { useMyPermissions } from "../../util/useMyPermissions";
import { C } from "./boardShared";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function BoardPage() {
    const { boardTypeId, postId } = useParams();
    const history = useHistory();
    const { isAdmin } = useMyPermissions();

    const [types, setTypes] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(true);
    const [myName, setMyName] = useState(null);
    /** 목록 강제 재조회 트리거 — 글 작성·삭제 후 목록 건수·최신 글을 맞춘다. */
    const [reloadKey, setReloadKey] = useState(0);

    const [compose, setCompose] = useState(null);   // { mode, post? } | null
    const [typeDialog, setTypeDialog] = useState(null); // { boardType? } | null

    /** 관리자는 숨김 게시판까지 본다 — 숨긴 게시판을 되살릴 경로가 필요하다. */
    const loadTypes = useCallback(async () => {
        setLoadingTypes(true);
        try {
            const list = await boardApi.listTypes(isAdmin);
            setTypes(Array.isArray(list) ? list : []);
        } catch {
            notify("게시판 목록을 불러오지 못했습니다", "error");
            setTypes([]);
        } finally {
            setLoadingTypes(false);
        }
    }, [isAdmin]);

    useEffect(() => { loadTypes(); }, [loadTypes]);

    useEffect(() => {
        api.get(`${API_BASE}/employees/me`).then(d => setMyName(d?.name)).catch(() => { });
    }, []);

    const selectedType = useMemo(
        () => types.find(t => String(t.id) === String(boardTypeId)) || null,
        [types, boardTypeId]);

    const bumpReload = () => setReloadKey(k => k + 1);

    const goHome = () => history.push("/board");
    const goBoard = (typeId) => history.push(`/board/${typeId}`);
    const goPost = (typeId, id) => history.push(`/board/${typeId}/${id}`);

    /**
     * 주소에 있는 게시판이 목록에 없으면 홈으로 되돌린다.
     *
     * <p>게시판이 삭제·숨김된 뒤 북마크로 들어오는 경우다. 그냥 두면 우측이 영구 빈 화면이 되어
     * "게시판이 안 열린다" 로 보인다. 목록 로딩 중에는 판단하지 않는다(로딩 중엔 항상 못 찾는다).
     */
    useEffect(() => {
        if (loadingTypes || !boardTypeId) return;
        if (!types.some(t => String(t.id) === String(boardTypeId))) {
            notify("게시판을 찾을 수 없습니다. 목록으로 이동합니다.", "warning");
            history.replace("/board");
        }
    }, [loadingTypes, types, boardTypeId, history]);

    return (
        <Box sx={{
            display: "flex", height: "100%", bgcolor: C.surface, overflow: "hidden",
            // 채널과 같은 full-bleed — 좌측 pane 이 메인 사이드바에 딱 붙는다(App.js 의 isFullBleedPage).
        }}>
            {loadingTypes ? (
                <Box sx={{ width: 250, borderRight: `1px solid ${C.border}` }}>
                    <LoadingDots compact />
                </Box>
            ) : (
                <BoardSidebar
                    types={types}
                    selectedTypeId={boardTypeId ?? null}
                    onSelectHome={goHome}
                    onSelect={goBoard}
                    isAdmin={isAdmin}
                    onCreateType={() => setTypeDialog({})}
                    onEditType={(t) => setTypeDialog({ boardType: t })}
                />
            )}

            <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", bgcolor: C.bg }}>
                {postId ? (
                    <BoardPostDetail
                        // key — 다른 글로 이동할 때 상세를 새로 마운트해 첨부 접힘·미리보기 상태가 남지 않게.
                        key={postId}
                        postId={Number(postId)}
                        boardTypes={types}
                        myName={myName}
                        reloadKey={reloadKey}
                        onBack={() => goBoard(boardTypeId)}
                        onOpenPost={(id) => goPost(boardTypeId, id)}
                        onEdit={(post) => setCompose({ mode: "edit", post })}
                        onReply={(post) => setCompose({ mode: "reply", post })}
                        onChanged={bumpReload}
                    />
                ) : selectedType ? (
                    <BoardPostList
                        boardType={selectedType}
                        reloadKey={reloadKey}
                        onOpenPost={(id) => goPost(selectedType.id, id)}
                        onCompose={() => setCompose({ mode: "create" })}
                    />
                ) : (
                    <BoardHome
                        types={types}
                        onOpenBoard={goBoard}
                        onOpenPost={goPost}
                    />
                )}
            </Box>

            <BoardPostComposeDialog
                open={Boolean(compose)}
                mode={compose?.mode}
                post={compose?.post}
                boardType={selectedType}
                isAdmin={isAdmin}
                onClose={() => setCompose(null)}
                onSaved={(saved) => {
                    bumpReload();
                    loadTypes();          // 게시판별 건수 배지 갱신
                    // 새 글·답글은 바로 그 글로 이동한다 — 목록에서 다시 찾게 하지 않는다.
                    if (compose?.mode !== "edit" && saved?.id) {
                        goPost(saved.boardTypeId ?? selectedType?.id, saved.id);
                    }
                }}
            />

            <BoardTypeDialog
                open={Boolean(typeDialog)}
                boardType={typeDialog?.boardType}
                onClose={() => setTypeDialog(null)}
                onSaved={loadTypes}
                onDeleted={() => { loadTypes(); goHome(); }}
            />
        </Box>
    );
}
